import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import Stack from 'components/layout/stack';
import { computed } from 'mobx';
import { LearningPathStore } from '@seedlang/stores';
import ProgressBar from 'components/progress_bar';

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled(Stack)`
  padding: 10px;
  font-size: 16px;
  border: 1px solid #888;
  border-radius: 10px;
  cursor: pointer;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  padding-bottom: 15px;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ChevronIcon = styled.i`
  font-family: FontAwesome;
  font-size: 20px;
  font-weight: bold;
  color: #444;
`;

const OptionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 100%;
  width: max-content;
  background: white;
  border: 1px solid #888;
  border-radius: 10px;
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.25);
`;

const Option = styled.div`
  padding: 8px 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  ${props => !props.selected && `
    &:hover {
      background: ${props.accentColor}10;
    }
  `}
  ${props => props.selected && `
    background: ${props.accentColor}20;
  `}
`;

const ProgressText = styled.div`
  flex: 1;
  text-align: right;
  color: #5b5b5b;
`;

const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 5px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  overflow: hidden;
`;
class LearningPathDropdown extends React.Component {
  static propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
    this.selectRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = event => {
    if (this.selectRef.current && !this.selectRef.current.contains(event.target)) {
      this.setState({ isOpen: false });
    }
  };

  toggleOpen = () => {
    if (!this.props.disabled) {
      this.setState(prev => ({ isOpen: !prev.isOpen }));
    }
  };

  handleSelect = id => {
    this.props.onChange(id);
    this.setState({ isOpen: false });
  };

  @computed get options() {
    if (!LearningPathStore.hasIndexData) return [];

    return LearningPathStore.indexData.map(item => {
      return {
        id: item.id,
        content: item.name,
        completedWorksheetsCount: item.completedWorksheetsCount || 0,
        totalWorksheetsCount: item.worksheetsCount || 0,
      };
    });
  }

  @computed get selectedOption() {
    return this.options.find(option => option.id === this.props.value);
  }

  render() {
    if (!this.selectedOption) return null;

    return (
      <SelectWrapper ref={this.selectRef}>
        <SelectButton
          onClick={this.toggleOpen}
          inline
          gap='xs'
        >
          <ContentWrapper>
            {this.selectedOption ? (
              this.selectedOption.content
            ) : (
              'Select a learning path'
            )}
          </ContentWrapper>
          <ChevronIcon className='fa fa-angle-down' />
          <ProgressText>{this.selectedOption.completedWorksheetsCount} / {this.selectedOption.totalWorksheetsCount}</ProgressText>
          <ProgressBarWrapper>
            <ProgressBar
              background='#CCC'
              current={this.selectedOption.completedWorksheetsCount}
              total={this.selectedOption.totalWorksheetsCount}
              width='100%'
              height='5px'
              borderBottomLeftRadius='10px'
              borderBottomRightRadius='10px'
            />
          </ProgressBarWrapper>
        </SelectButton>
        {this.state.isOpen && (
          <OptionsList>
            {this.options.map(option => (
              <Option
                key={option.id}
                onClick={() => this.handleSelect(option.id)}
                selected={option.id === this.props.value}
                accentColor={AppUI.site.accentColor}
              >
                {option.content}
                <ProgressText>{option.completedWorksheetsCount} / {option.totalWorksheetsCount}</ProgressText>
              </Option>
            ))}
          </OptionsList>
        )}
      </SelectWrapper>
    );
  }
}

export default LearningPathDropdown;